import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { SettingsFormService } from '../../settings/directives/tbSettingsCoordinator';
import { ISiteAdminSettings } from '../../settings/types/ISiteSettingsForSiteAdmin';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { getAppStore } from '../AppStore';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { SessionServerSelectors, SessionSiteSelectors } from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';
import { SiteSettingsStyles } from './styles/SiteSettingsStyles';
import { RUN_NOW_SECTION_CHECKBOX } from '../../settings/general-settings/components/sections/RunNowSection';

interface RunNowEnabledSiteSettingAngularProps {
    $q: ng.IQService;
    sessionManagementService: SessionManagementService;
    formService: SettingsFormService;
}

interface RunNowEnabledSiteSettingOwnProps {
    siteAdminSettings: ISiteAdminSettings;
}

interface RunNowEnabledSiteSettingState {
    isEnabled: boolean;
}

export type RunNowEnabledSiteSettingProps = RunNowEnabledSiteSettingOwnProps
    & RunNowEnabledSiteSettingAngularProps;

/**
 * @deprecated replaced with the RunNowSection
 * This file can be removed when the React settings goes live
 */
export class RunNowEnabledSiteSetting extends React.Component<RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingState> {
    private shouldRender: boolean;

    constructor(props: RunNowEnabledSiteSettingProps) {
        super(props);

        // We only render the option in certain circumstances
        const isServerAdmin = this.props.sessionManagementService.isSessionServerAdmin();
        const isOnline = this.props.sessionManagementService.isOnlineEnvironment();
        const isSiteAdmin = this.props.sessionManagementService.isSessionSiteAdmin();
        const isAllowedToChangeRunNowSetting = isServerAdmin
            || isOnline && isSiteAdmin;
        this.shouldRender = FeatureFlags.enableRunNowEnabled && isAllowedToChangeRunNowSetting;

        if (!this.shouldRender) {
            // If we don't render we don't register as part of the form or set any state
            return;
        }
        this.state = {
            isEnabled: SessionSiteSelectors.isRunNowEnabledOnSite(getAppStore().getState()),
        };

        // Gets the backend state and populates the component state with it
        this.openConfirmationDialog = this.openConfirmationDialog.bind(this);

        // Aggravatingly, we need to register with the form service in order to actually
        // revert when someone hits the revert button
        const formState = {
            formController: {
                $pristine: true,
                $dirty: false,
                $valid: true,
                $invalid: true,
                $submitted: false,
                $error: {},
                $addControl: () => { },
                $removeControl: () => { },
                $setValidity: () => { },
                $setDirty: () => { },
                $setPristine: () => { },
                $setSubmitted: () => { },
                $setUntouched: () => { },
                $commitViewValue: () => { },
                $rollbackViewValue: () => { },
            },
            cancel: () => {
                this.setState({
                    isEnabled: SessionSiteSelectors.isRunNowEnabledOnSite(getAppStore().getState()),
                });
            },
            // Annoyingly save is called after the update occurs
            // so we have to update the shared object that holds
            // the form state
            save: () => this.props.$q.resolve(),

            // "Optional" but actually required
            isBusy: () => false,
            isFormUnchanged: () => {
                return SessionSiteSelectors.isRunNowEnabledOnSite(getAppStore().getState()) === this.state.isEnabled;
            },
            isValid: () => true,
        };
        this.props.formService.registerFormStateByName(formState, 'Run Now Settings');
    }
    private openConfirmationDialog(): void {
        const confirmGlobalToggleTitle = this.state.isEnabled
            ? Messages.runNowDisable()
            : Messages.runNowEnable();
        const confirmGlobalToggleText = this.state.isEnabled
            ? Messages.runNowDisableSiteConfirmationText()
            : Messages.runNowEnableSiteConfirmationText();
        const modal = ReactModalService.open({
            title: confirmGlobalToggleTitle,
            testId: 'globally-toggle-runnow-dialog',
            type: DialogType.NormalWithTitleBar,
            positioningBehavior: DialogPositioningBehavior.Centered,
            dialogContent: <>{ confirmGlobalToggleText }</>,
            footerContent: <DialogFooterButtonsWidget
                buttons={[
                    {
                        handleClick: () => {
                            modal.close();
                        },
                        buttonType: ButtonType.Grey,
                        children: Messages.runNowDialogCancel(),
                        testId: 'footer-cancel'
                    }, {
                        handleClick: () => {
                            const runNowEnabled = !this.state.isEnabled;
                            // We actually update the underlying form state by modifying the props
                            // this is really icky, but that's the way it has to be with angular
                            this.props.siteAdminSettings.runNowEnabled = runNowEnabled;

                            this.setState({
                                isEnabled: runNowEnabled
                            }, () => {
                                // We then have to tell angular to actually check that the form changed
                                const context = ServiceLocator.getAngularActionContext();
                                context.scope.$apply();
                            });
                            modal.close();
                        },
                        buttonType: ButtonType.Go,
                        children: Messages.runNowDialogYes(),
                        testId: 'confirm-action-dialog-confirm'
                    }
                ]}
                testId="footer"
            />
        });
    }

    render() {
        if (!this.shouldRender || !this.state) {
            return null;
        }
        return <>
            <div style={SiteSettingsStyles.HeaderStyle}>{Messages.runNowEnabledSettingHeader()}</div>
            <div style={SiteSettingsStyles.BodyStyle}>
                <p>{Messages.runNowEnabledSettingDescription()}</p>
                <CheckBoxWithLabelWidget
                    containerStyle={SiteSettingsStyles.CheckboxStyle}
                    checked={SessionServerSelectors.isRunNowEnabledOnServer(getAppStore().getState()) && this.state.isEnabled}
                    handleChange={this.openConfirmationDialog}
                    testId={RUN_NOW_SECTION_CHECKBOX}
                    label={Messages.runNowGlobalToggleDialogCheckboxLabel()}
                    // undefined server setting shouldn't stop us from interacting with the form
                    disabled={SessionServerSelectors.isRunNowEnabledOnServer(getAppStore().getState()) === false}
                />
            </div>
        </>;
    }
}

export namespace RunNowEnabledSiteSettingAngularProvider {
    function getAngularServicesWithServiceLocator(): RunNowEnabledSiteSettingProps {
        return {
            $q: ServiceLocator.get('$q'),
            sessionManagementService: getSessionManagementServiceInstance(),
            formService: ServiceLocator.get('SettingsFormService')
        } as any;
    }
    function selectPropsFromAngularServices(angularServices: RunNowEnabledSiteSettingProps) {
        return angularServices;
    }

    export const HOC = angularProviderHOC<RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingProps, RunNowEnabledSiteSettingProps>({
        getAngularServicesWithServiceLocator,
        selectPropsFromAngularServices,
    });
}

export const RunNowEnabledSiteSettingAng = RunNowEnabledSiteSettingAngularProvider.HOC(RunNowEnabledSiteSetting);
